import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, TextField, Button, Alert, Container } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneInput from '../components/PhoneInput';
import Footer from '../components/landing/Footer';
import SEO from '../components/SEO';
import { PAGE_CONTAINER_SX } from '../components/landing/pageLayout';

const CONTACT_EMAIL = 'qdexsales@qdexconsulting.com';
const CONTACT_PHONE = '+91 9443295287';
const CONTACT_ADDRESS = 'QDEX Consulting, 31A, Greenwood City, Navalur, Chennai';

export default function Contact() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    phone_country_code: '+1',
    requirement: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleCountryCodeChange = (value) => {
    setForm({ ...form, phone_country_code: value });
  };

  // Phone validation function (same as signup page)
  const validatePhone = (phone) => {
    // Only check for minimum 5 digits, allow any format
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 5;
  };

  // Name validation function (same as signup page)
  const validateName = (name) => {
    // Name should contain only letters, spaces, hyphens, apostrophes, and periods
    // Must be at least 4 characters, can have multiple words
    const nameRegex = /^[a-zA-Z\s\-'\.]{4,50}$/;
    return nameRegex.test(name.trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!form.firstName || !form.lastName || !form.email || !form.company || !form.requirement) {
      setError('Please fill all required fields.');
      return;
    }

    // Validate names
    if (!validateName(form.firstName)) {
      setError('Please enter a valid first name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)');
      return;
    }

    if (!validateName(form.lastName)) {
      setError('Please enter a valid last name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)');
      return;
    }

    // Validate phone if provided
    if (form.phone && !validatePhone(form.phone)) {
      setError('Please enter a valid phone number (at least 5 digits)');
      return;
    }
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setSuccess(true);
      if (window.fbq) window.fbq('track', 'Contact');
      setForm({ firstName: '', lastName: '', email: '', company: '', phone: '', phone_country_code: '+1', requirement: '' });
    } catch (err) {
      setError('Failed to send your message. Please try again.');
    }
    setLoading(false);
  };

  return (
    <>
      <SEO
        title="Book Sniffr Demo | Contact Us - Sniffr.ai"
        description="Get in touch with the Sniffr.ai team. Book a demo, ask questions, or reach out to our sales team. AI-powered code review and UAT automation for engineering teams."
        keywords="contact Sniffr.ai, book sniffr demo, AI code review contact, UAT automation demo"
        url="/contact"
      />
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', color: '#1f1f1f', py: { xs: 6, sm: 8, md: 10 } }}>
      <Container maxWidth="xl" sx={PAGE_CONTAINER_SX}>
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#1f1f1f', fontFamily: 'MD IO, monospace' }}>
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280', fontSize: '16px', fontFamily: 'Segoe UI, monospace' }}>
            Get in touch with our team. We'd love to hear from you.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700} gutterBottom align="start" sx={{ color: '#1f1f1f', mb: 4 }}>
                  Get in Touch
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <Box display="flex" alignItems="center">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      mr: 2
                    }}>
                      <EmailIcon sx={{ color: '#1f1f1f', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px', mb: 0.5 }}>
                        Email
                      </Typography>
                      <Typography 
                        component="a" 
                        href={`mailto:${CONTACT_EMAIL}`} 
                        sx={{ 
                          color: '#1f1f1f', 
                          textDecoration: 'none',
                          fontWeight: 500,
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {CONTACT_EMAIL}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      mr: 2
                    }}>
                      <PhoneIcon sx={{ color: '#1f1f1f', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px', mb: 0.5 }}>
                        Phone
                      </Typography>
                      <Typography 
                        component="a" 
                        href={`tel:${CONTACT_PHONE}`} 
                        sx={{ 
                          color: '#1f1f1f', 
                          textDecoration: 'none',
                          fontWeight: 500,
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {CONTACT_PHONE}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      mr: 2,
                      mt: 0.5
                    }}>
                      <LocationOnIcon sx={{ color: '#1f1f1f', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px', mb: 0.5 }}>
                        Address
                      </Typography>
                      <Typography sx={{ color: '#1f1f1f', fontWeight: 500, lineHeight: 1.5 }}>
                        {CONTACT_ADDRESS}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#1f1f1f', mb: 3 }}>
                  Send us a Message
                </Typography>
                
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>Your message has been sent! We'll get back to you soon.</Alert>}
                
                <form onSubmit={handleSubmit} autoComplete="on">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="First Name *"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        fullWidth
                        required
                        autoComplete="given-name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: form.firstName ?
                                (validateName(form.firstName) ? '#4caf50' : '#f44336') : '#e0e0e0'
                            },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14,
                            height: '50px'
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1.5,
                            fontSize: 14,
                            height: '50px'
                          }
                        }}
                      />
                      {form.firstName && !validateName(form.firstName) && (
                        <Typography variant="caption" sx={{
                          color: '#f44336',
                          fontWeight: 'bold',
                          display: 'block',
                          mt: 0.5,
                          fontSize: 12
                        }}>
                          ❌ Please enter a valid name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Name *"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        fullWidth
                        required
                        autoComplete="family-name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: form.lastName ?
                                (validateName(form.lastName) ? '#4caf50' : '#f44336') : '#e0e0e0'
                            },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14,
                            height: '50px'
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1.5,
                            fontSize: 14,
                            height: '50px'
                          }
                        }}
                      />
                      {form.lastName && !validateName(form.lastName) && (
                        <Typography variant="caption" sx={{
                          color: '#f44336',
                          fontWeight: 'bold',
                          display: 'block',
                          mt: 0.5,
                          fontSize: 12
                        }}>
                          ❌ Please enter a valid name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email *"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        fullWidth
                        required
                        autoComplete="email"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14,
                            height: '50px'
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1.5,
                            fontSize: 14,
                            height: '50px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Company *"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        fullWidth
                        required
                        autoComplete="organization"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14,
                            height: '50px'
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1.5,
                            fontSize: 14,
                            height: '50px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PhoneInput
                        value={form.phone}
                        countryCode={form.phone_country_code}
                        onChange={handlePhoneChange}
                        onCountryCodeChange={handleCountryCodeChange}
                        label="Phone"
                        autoComplete="tel"
                        error={form.phone && !validatePhone(form.phone)}
                        helperText={form.phone && !validatePhone(form.phone) ? 'Please enter at least 5 digits' : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14,
                            height: '50px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Your Requirement *"
                        name="requirement"
                        value={form.requirement}
                        onChange={handleChange}
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        autoComplete="off"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                            borderRadius: 1,
                            bgcolor: '#fff',
                            fontSize: 14
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ 
                      mt: 4, 
                      fontWeight: 600, 
                      borderRadius: 1, 
                      py: 1.5,
                      bgcolor: '#000',
                      color: '#fff',
                      textTransform: 'none',
                      fontSize: 16,
                      '&:hover': { bgcolor: '#333' },
                      '&:disabled': { bgcolor: '#ccc' }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
    <Footer />
    </>
  );
} 